import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Plus } from "lucide-react";
import agentQaApi from "../../../../api/agentQaApi";
import shopApi from "../../../../api/shopApi";
import "../ChatbotSettingsPage/styles.css";

const QaBotPage = () => {
  const auth = useSelector((state) => state.auth);
  const accountId = auth?.user?.accountId;
  const staffId = auth?.user?.id;

  const [entries, setEntries] = useState([]);
  const [shopsList, setShopsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newRow, setNewRow] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkShopIds, setBulkShopIds] = useState([]);
  const [shopDropdownOpen, setShopDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Load data
  const loadData = useCallback(async () => {
    if (!accountId) return;
    try {
      setLoading(true);
      const [res, shopsRes] = await Promise.all([
        agentQaApi.getAll(accountId),
        shopApi.getShops({ accountId, staffId }),
      ]);

      if (res.status === 1) {
        const sorted = [...res.data].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        setEntries(sorted);
      }

      if (Array.isArray(shopsRes)) {
        setShopsList(shopsRes);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accountId, staffId]);

  useEffect(() => {
    document.title = "Cài đặt QA Bot";
    loadData();
  }, [loadData, accountId]);

  // Handle dropdown toggle with position calculation
  const handleDropdownToggle = (id, buttonElement) => {
    if (shopDropdownOpen === id) {
      setShopDropdownOpen(null);
    } else {
      if (buttonElement) {
        const rect = buttonElement.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }
      setShopDropdownOpen(id);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside dropdown menus
      const dropdownMenus = document.querySelectorAll(".shop-dropdown-menu");
      let clickedInsideDropdown = false;
      dropdownMenus.forEach((menu) => {
        if (menu.contains(event.target)) {
          clickedInsideDropdown = true;
        }
      });
      // Check if click is on dropdown toggle button
      const toggleButtons = document.querySelectorAll(".add-button"); // using add-button class for toggle
      let clickedOnToggle = false;
      toggleButtons.forEach((btn) => {
        if (btn.contains(event.target)) {
          clickedOnToggle = true;
        }
      });
      if (!clickedInsideDropdown && !clickedOnToggle) {
        setShopDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Add new row
  const handleAddNew = () => {
    setNewRow({
      question: "",
      answer: "",
      isActive: true,
    });
    setEditingId(null);
  };

  // Save new row
  const handleSaveNew = async () => {
    if (!newRow.question.trim()) {
      alert("Vui lòng nhập câu hỏi");
      return;
    }
    if (!newRow.answer.trim()) {
      alert("Vui lòng nhập câu trả lời");
      return;
    }
    try {
      const res = await agentQaApi.create({
        question: newRow.question,
        answer: newRow.answer,
        isActive: newRow.isActive,
        accountId,
      });

      if (res.status === 1) {
        setEntries((prev) => [res.data, ...prev]);
        setNewRow(null);
      } else {
        alert(res.message || "Lỗi khi tạo");
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleCancelNew = () => {
    setNewRow(null);
  };

  // Start editing
  const handleEdit = (entry) => {
    setEditingId(entry.id);
    setEditData({
      question: entry.question || "",
      answer: entry.answer || "",
      isActive: entry.isActive,
    });
    setNewRow(null);
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editData.question.trim()) {
      alert("Vui lòng nhập câu hỏi");
      return;
    }
    if (!editData.answer.trim()) {
      alert("Vui lòng nhập câu trả lời");
      return;
    }
    try {
      const res = await agentQaApi.update(editingId, {
        question: editData.question,
        answer: editData.answer,
        isActive: editData.isActive,
      });

      if (res.status === 1) {
        setEntries((prev) =>
          prev.map((e) => (e.id === editingId ? res.data : e)),
        );
        setEditingId(null);
        setEditData(null);
      } else {
        alert(res.message || "Lỗi khi cập nhật");
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData(null);
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Bạn có chắc muốn xoá?")) return;
    try {
      const res = await agentQaApi.delete(id);
      if (res.status === 1) {
        setEntries((prev) => prev.filter((e) => e.id !== id));
        // Remove from selection if deleted
        if (selectedIds.has(id)) {
          const newSelected = new Set(selectedIds);
          newSelected.delete(id);
          setSelectedIds(newSelected);
        }
      } else {
        alert(res.message || "Lỗi khi xoá");
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(entries.map((e) => e.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkShopToggle = (shopId) => {
    if (bulkShopIds.includes(shopId)) {
      setBulkShopIds(bulkShopIds.filter((id) => id !== shopId));
    } else {
      setBulkShopIds([...bulkShopIds, shopId]);
    }
  };

  const handleBulkSave = async () => {
    if (selectedIds.size === 0) return;
    if (bulkShopIds.length === 0) {
      alert("Vui lòng chọn ít nhất một tài khoản");
      return;
    }

    try {
      const ids = Array.from(selectedIds);
      const res = await agentQaApi.bulkUpdateShops(ids, bulkShopIds);

      if (res.status === 1) {
        // Update local state
        setEntries((prev) =>
          prev.map((e) =>
            selectedIds.has(e.id) ? { ...e, shopIds: bulkShopIds } : e,
          ),
        );
        setSelectedIds(new Set());
        setBulkShopIds([]);
        setShopDropdownOpen(null);
        alert("Cập nhật thành công!");
      } else {
        alert(res.message || "Lỗi khi cập nhật");
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Get shop IDs display text
  const getShopIdsText = (shopIds) => {
    if (!shopIds || shopIds.length === 0) return "Chưa chọn tài khoản";
    // Map IDs to names
    const names = shopIds.map((id) => {
      const shop = shopsList.find((s) => s.id === id);
      return shop ? shop.name : id;
    });
    return names.join(", ");
  };

  if (loading) {
    return (
      <div className="chatbot-settings-page">
        <div className="loading-container">Đang tải...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chatbot-settings-page">
        <div className="error-container">Lỗi: {error}</div>
      </div>
    );
  }

  return (
    <div className="chatbot-settings-page">
      <h1>Cài đặt QA Bot</h1>

      {!newRow && (
        <div className="flex gap-2 mb-4">
          <button className="add-button" onClick={handleAddNew}>
            <Plus size={18} />
            Thêm mới
          </button>

          <div
            className="shop-dropdown"
            style={{ display: "inline-block", marginLeft: "10px" }}
          >
            <button
              className={`add-button bg-blue-600 hover:bg-blue-700 ${selectedIds.size === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={(e) => {
                if (selectedIds.size === 0) return;
                handleDropdownToggle("bulk-shops", e.currentTarget);
              }}
            >
              Thêm vào Zalo{" "}
              {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
              <span className="dropdown-arrow">▼</span>
            </button>

            {shopDropdownOpen === "bulk-shops" && (
              <div
                className="shop-dropdown-menu"
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                  minWidth: "200px",
                }}
              >
                <div className="p-2 font-medium border-b text-sm">
                  Chọn tài khoản áp dụng:
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {shopsList.length === 0 ? (
                    <div className="p-2 text-muted">Không có tài khoản</div>
                  ) : (
                    shopsList.map((shop) => (
                      <label key={shop.id} className="shop-checkbox">
                        <input
                          type="checkbox"
                          checked={bulkShopIds.includes(shop.id)}
                          onChange={() => handleBulkShopToggle(shop.id)}
                        />
                        {shop.name}
                      </label>
                    ))
                  )}
                </div>
                <div className="p-2 border-t flex justify-end gap-2">
                  <button
                    className="px-3 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                    onClick={() => setShopDropdownOpen(null)}
                  >
                    Đóng
                  </button>
                  <button
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    onClick={handleBulkSave}
                  >
                    Lưu
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="settings-table-container">
        <table className="settings-table">
          <thead>
            <tr>
              <th style={{ width: 40, textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={
                    entries.length > 0 && selectedIds.size === entries.length
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th>Hành động</th>
              <th>Câu hỏi</th>
              <th>Trả lời</th>
              <th>Tài khoản Zalo</th>
              <th>Trạng thái</th>
              <th>Lượt khớp</th>
              <th>Ngày tạo</th>
            </tr>
          </thead>
          <tbody>
            {/* New row */}
            {newRow && (
              <tr className="new-row">
                <td></td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-save" onClick={handleSaveNew}>
                      Lưu
                    </button>
                    <button className="btn-cancel" onClick={handleCancelNew}>
                      Huỷ
                    </button>
                  </div>
                </td>
                <td>
                  <textarea
                    className="textarea-field"
                    value={newRow.question}
                    onChange={(e) =>
                      setNewRow({ ...newRow, question: e.target.value })
                    }
                    placeholder="Câu hỏi mẫu..."
                    rows={2}
                  />
                </td>
                <td>
                  <textarea
                    className="textarea-field"
                    value={newRow.answer}
                    onChange={(e) =>
                      setNewRow({ ...newRow, answer: e.target.value })
                    }
                    placeholder="Câu trả lời..."
                    rows={2}
                  />
                </td>
                <td className="text-muted">-</td>
                <td>
                  <div
                    className={`toggle-switch ${newRow.isActive ? "active" : ""}`}
                    onClick={() =>
                      setNewRow({ ...newRow, isActive: !newRow.isActive })
                    }
                  />
                </td>
                <td className="text-muted">0</td>
                <td className="date-cell">-</td>
              </tr>
            )}

            {/* Existing rows */}
            {entries.map((entry) => (
              <tr key={entry.id}>
                {editingId === entry.id ? (
                  <>
                    {/* Edit mode */}
                    <td></td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-save" onClick={handleSaveEdit}>
                          Lưu
                        </button>
                        <button
                          className="btn-cancel"
                          onClick={handleCancelEdit}
                        >
                          Huỷ
                        </button>
                      </div>
                    </td>
                    <td>
                      <textarea
                        className="textarea-field"
                        value={editData.question}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            question: e.target.value,
                          })
                        }
                        rows={2}
                      />
                    </td>
                    <td>
                      <textarea
                        className="textarea-field"
                        value={editData.answer}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            answer: e.target.value,
                          })
                        }
                        rows={2}
                      />
                    </td>
                    <td>{getShopIdsText(entry.shopIds)}</td>
                    <td>
                      <div
                        className={`toggle-switch ${editData.isActive ? "active" : ""}`}
                        onClick={() =>
                          setEditData({
                            ...editData,
                            isActive: !editData.isActive,
                          })
                        }
                      />
                    </td>
                    <td>{entry.hitCount || 0}</td>
                    <td className="date-cell">{formatDate(entry.createdAt)}</td>
                  </>
                ) : (
                  <>
                    {/* View mode */}
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(entry.id)}
                        onChange={() => handleSelectRow(entry.id)}
                      />
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-edit"
                          onClick={() => handleEdit(entry)}
                        >
                          Sửa
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(entry.id)}
                        >
                          Xoá
                        </button>
                      </div>
                    </td>
                    <td>
                      <div
                        style={{
                          maxWidth: 250,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entry.question || "-"}
                      </div>
                    </td>
                    <td>
                      <div
                        style={{
                          maxWidth: 250,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entry.answer || "-"}
                      </div>
                    </td>
                    <td>{getShopIdsText(entry.shopIds)}</td>
                    <td>
                      <span
                        className={`status-badge ${entry.isActive ? "active" : "inactive"}`}
                      >
                        {entry.isActive ? "Bật" : "Tắt"}
                      </span>
                    </td>
                    <td>{entry.hitCount || 0}</td>
                    <td className="date-cell">{formatDate(entry.createdAt)}</td>
                  </>
                )}
              </tr>
            ))}

            {entries.length === 0 && !newRow && (
              <tr>
                <td colSpan="8" className="empty-container">
                  Chưa có câu hỏi nào. Bấm "Thêm mới" để tạo.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QaBotPage;
